import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../utils/api';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    authApi.getMe().then(({ data }) => {
      const u = data.data;
      setProfile(u);
      setForm({
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        phone: u.phone || '',
        email: u.email || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await authApi.updateProfile(form);
      const resData = res.data;
      await refreshUser();

      if (resData.emailVerificationSent) {
        setMessage({ type: 'info', text: `Verification link sent to ${form.email}. Your email will be updated once you verify.` });
        // Reset email field to current (unchanged) email
        setForm(f => ({ ...f, email: resData.data.email }));
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully.' });
      }
      setProfile(resData.data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update profile.' });
    }
    setSaving(false);
  };

  const handleResendVerification = async () => {
    setResending(true);
    setMessage(null);
    try {
      await authApi.resendVerification();
      setMessage({ type: 'success', text: 'Verification email sent. Check your inbox.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to resend verification email.' });
    }
    setResending(false);
  };

  if (loading) return <div className="spinner" />;

  return (
    <div className="page">
      <div className="page-content">
          <div className="page-header">
            <h1>Profile</h1>
            <p>Manage your account information</p>
          </div>

          {message && (
            <div className={`alert alert-${message.type}`}>{message.text}</div>
          )}

          {profile && !profile.is_verified && (
            <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <span>Your email is not verified. Please check your inbox.</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleResendVerification}
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend Verification'}
              </button>
            </div>
          )}

          <div className="card">
            <form onSubmit={handleSave}>
              <div className="grid-2col">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
                {form.email !== profile?.email && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                    A verification link will be sent to the new email address
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <div className="grid-2col">
                <div className="form-group">
                  <label>Role</label>
                  <input value={profile?.role?.replace('_', ' ') || ''} disabled style={{ textTransform: 'capitalize' }} />
                </div>
                <div className="form-group">
                  <label>Member Since</label>
                  <input
                    value={profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : ''}
                    disabled
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      </div>
  );
}
